import React, { useState } from "react";
import Page from "../../components/Page";
import {
  IconChefHat,
  IconPlus,
  IconPencil,
  IconTrash,
  IconVolume,
  IconCheck,
  IconX,
  IconAlertCircle,
  IconFilter,
} from "@tabler/icons-react";
import { iconStroke } from "../../config/config";
import { useCategories } from "../../controllers/settings.controller";
import {
  useKitchenDisplays,
  createKitchenDisplay,
  updateKitchenDisplay,
  deleteKitchenDisplay,
} from "../../controllers/kitchen_display.controller";
import { playKitchenOrderPickupReadySound, unlockAudio } from "../../helpers/SoundHelper";
import { useTheme } from "../../contexts/ThemeContext";
import toast from "react-hot-toast";

export default function KitchenDisplaysSettingsPage() {
  const { theme } = useTheme();
  const { data: kitchens, isLoading: isLoadingKitchens, mutate } = useKitchenDisplays();
  const { data: categories, isLoading: isLoadingCategories } = useCategories();

  const kitchenList = Array.isArray(kitchens) ? kitchens : (Array.isArray(kitchens?.data) ? kitchens.data : []);
  const allCategories = Array.isArray(categories) ? categories : (Array.isArray(categories?.data) ? categories.data : []);

  const [modalState, setModalState] = useState({
    isOpen: false,
    isEdit: false,
    id: null,
    name: "",
    description: "",
    selectedCategories: [], // array of category IDs as numbers/strings
    isActive: true,
    isSubmitting: false,
  });

  const handleOpenAddModal = () => {
    setModalState({
      isOpen: true,
      isEdit: false,
      id: null,
      name: "",
      description: "",
      selectedCategories: [],
      isActive: true,
      isSubmitting: false,
    });
  };

  const handleOpenEditModal = (kitchen) => {
    const catIds = (kitchen.categories || []).map((c) => Number(c));
    setModalState({
      isOpen: true,
      isEdit: true,
      id: kitchen.id,
      name: kitchen.name || "",
      description: kitchen.description || "",
      selectedCategories: catIds,
      isActive: kitchen.is_active === 1 || kitchen.is_active === true,
      isSubmitting: false,
    });
  };

  const handleToggleCategory = (catId) => {
    const idNum = Number(catId);
    setModalState((prev) => {
      const exists = prev.selectedCategories.includes(idNum);
      const updated = exists
        ? prev.selectedCategories.filter((id) => id !== idNum)
        : [...prev.selectedCategories, idNum];
      return { ...prev, selectedCategories: updated };
    });
  };

  const handleSelectAllCategories = () => {
    const allIds = allCategories.map((c) => Number(c.id));
    setModalState((prev) => ({ ...prev, selectedCategories: allIds }));
  };

  const handleClearAllCategories = () => {
    setModalState((prev) => ({ ...prev, selectedCategories: [] }));
  };

  const handleSubmitModal = async (e) => {
    e.preventDefault();
    if (!modalState.name.trim()) {
      toast.error("Please enter a kitchen name");
      return;
    }

    try {
      setModalState((prev) => ({ ...prev, isSubmitting: true }));
      toast.loading("Saving kitchen display...");

      const payload = {
        name: modalState.name.trim(),
        description: modalState.description.trim(),
        categories: modalState.selectedCategories,
        is_active: modalState.isActive ? 1 : 0,
      };

      if (modalState.isEdit) {
        await updateKitchenDisplay(modalState.id, payload);
        toast.dismiss();
        toast.success("Kitchen display updated successfully!");
      } else {
        await createKitchenDisplay(payload);
        toast.dismiss();
        toast.success("Kitchen display created successfully!");
      }

      await mutate();
      setModalState((prev) => ({ ...prev, isOpen: false, isSubmitting: false }));
    } catch (err) {
      toast.dismiss();
      console.error(err);
      toast.error(err?.response?.data?.message || "Failed to save kitchen display");
      setModalState((prev) => ({ ...prev, isSubmitting: false }));
    }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Are you sure you want to delete kitchen "${name}"?`)) {
      return;
    }

    try {
      toast.loading("Deleting kitchen display...");
      await deleteKitchenDisplay(id);
      toast.dismiss();
      toast.success("Kitchen display deleted successfully");
      await mutate();
    } catch (err) {
      toast.dismiss();
      console.error(err);
      toast.error(err?.response?.data?.message || "Failed to delete kitchen display");
    }
  };

  const handleTestAudio = () => {
    unlockAudio();
    playKitchenOrderPickupReadySound({
      tableTitle: "Table 1",
    });
    toast.success("🔊 Played test chime & voice: 'Pickup ready for Table 1!'");
  };

  return (
    <Page className="px-8 py-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 pb-4 border-b border-gray-100 dark:border-neutral-800">
        <div>
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-2xl bg-emerald-50 dark:bg-emerald-950/40 text-emerald-600 flex items-center justify-center">
              <IconChefHat size={24} stroke={iconStroke} />
            </div>
            <div>
              <h2 className="text-2xl font-bold tracking-tight">Kitchen Displays</h2>
              <p className="text-xs text-gray-500">
                Configure separate kitchen stations and assign categories to filter orders per kitchen screen.
              </p>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={handleTestAudio}
            className="rounded-xl border transition active:scale-95 px-3.5 py-2 flex items-center gap-2 text-xs font-semibold bg-white dark:bg-neutral-800 border-gray-200 dark:border-neutral-700 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-neutral-700"
            title="Test the pickup ready announcement chime and voice"
          >
            <IconVolume size={16} className="text-emerald-600" />
            <span>Test Ready Sound</span>
          </button>

          <button
            type="button"
            onClick={handleOpenAddModal}
            className="rounded-xl border transition active:scale-95 px-4 py-2 flex items-center gap-2 text-xs font-semibold bg-restro-green text-white border-restro-green hover:bg-emerald-700 shadow-sm"
          >
            <IconPlus size={16} />
            <span>Add Kitchen</span>
          </button>
        </div>
      </div>

      {/* Guidance Note */}
      <div className="mt-4 p-4 rounded-2xl bg-blue-50/70 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-900 text-xs text-blue-900 dark:text-blue-200 flex items-start gap-3">
        <IconAlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
        <div>
          <p className="font-bold mb-0.5">How Kitchen Displays & Category Filtering Work:</p>
          <ul className="list-disc list-inside space-y-0.5 text-blue-800/90 dark:text-blue-300">
            <li>
              Assign specific categories to a kitchen (e.g. <strong>Main Kitchen</strong> handles Burgers & Chowmein; <strong>Bar</strong> handles Beverages & Coffee).
            </li>
            <li>
              On the <strong>Kitchen Display</strong> screen, selecting that kitchen will automatically show <strong>only items from its assigned categories</strong>.
            </li>
            <li>
              When an item or order is marked complete, it announces <strong>"Pickup ready for Table [X]!"</strong> or <strong>"Pickup ready for Room [Y]!"</strong> with a notification chime.
            </li>
          </ul>
        </div>
      </div>

      {/* Kitchen Displays List */}
      <div className="mt-6">
        {isLoadingKitchens ? (
          <div className="py-12 text-center text-gray-400 text-sm">Loading kitchen stations...</div>
        ) : kitchenList.length === 0 ? (
          <div className="py-16 text-center border-2 border-dashed border-gray-200 dark:border-neutral-800 rounded-3xl p-8">
            <div className="w-16 h-16 mx-auto mb-4 rounded-3xl bg-gray-50 dark:bg-neutral-800 text-gray-400 flex items-center justify-center">
              <IconChefHat size={32} />
            </div>
            <h4 className="text-base font-bold text-gray-800 dark:text-gray-200">No Kitchen Displays Configured Yet</h4>
            <p className="text-xs text-gray-500 max-w-md mx-auto mt-1 mb-4">
              Add your first kitchen station (e.g., "Main Kitchen", "Bar Station", or "Pizza Station") and assign categories to it.
            </p>
            <button
              type="button"
              onClick={handleOpenAddModal}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-xs font-semibold bg-restro-green text-white hover:bg-emerald-700 transition"
            >
              <IconPlus size={16} />
              Add First Kitchen
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {kitchenList.map((k) => {
              const assignedCount = (k.categories || []).length;
              const assignedCategoryNames = (k.categories || []).map((catId) => {
                const found = allCategories.find((c) => Number(c.id) === Number(catId));
                return found ? found.title : `Category #${catId}`;
              });

              return (
                <div
                  key={k.id}
                  className="rounded-2xl border border-gray-100 dark:border-neutral-800 bg-white dark:bg-neutral-800/80 p-5 shadow-sm hover:shadow-md transition flex flex-col justify-between"
                >
                  <div>
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <div className="flex items-center gap-2.5">
                        <div className="w-9 h-9 rounded-xl bg-emerald-50 dark:bg-emerald-950/40 text-emerald-600 flex items-center justify-center flex-shrink-0">
                          <IconChefHat size={20} />
                        </div>
                        <div>
                          <h3 className="font-bold text-base text-gray-900 dark:text-white leading-tight">
                            {k.name}
                          </h3>
                          {k.description && (
                            <p className="text-xs text-gray-400 line-clamp-1">{k.description}</p>
                          )}
                        </div>
                      </div>

                      <span
                        className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${
                          k.is_active === 1 || k.is_active === true
                            ? "bg-emerald-50 text-emerald-700 dark:bg-emerald-950/60 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-800"
                            : "bg-gray-100 text-gray-500 dark:bg-neutral-700 dark:text-gray-400"
                        }`}
                      >
                        {k.is_active === 1 || k.is_active === true ? "Active" : "Inactive"}
                      </span>
                    </div>

                    <div className="mt-3 pt-3 border-t border-gray-50 dark:border-neutral-700/60">
                      <div className="flex items-center justify-between text-xs text-gray-500 mb-2">
                        <span className="font-semibold flex items-center gap-1">
                          <IconFilter size={14} className="text-emerald-600" />
                          Assigned Categories ({assignedCount})
                        </span>
                        {assignedCount === 0 && (
                          <span className="text-[11px] text-amber-600 bg-amber-50 dark:bg-amber-950/40 px-2 py-0.5 rounded">
                            Shows All Items
                          </span>
                        )}
                      </div>

                      <div className="flex flex-wrap gap-1.5 max-h-24 overflow-y-auto">
                        {assignedCount === 0 ? (
                          <span className="text-xs text-gray-400 italic">
                            No specific categories assigned. All tenant orders will show here.
                          </span>
                        ) : (
                          assignedCategoryNames.map((name, i) => (
                            <span
                              key={i}
                              className="px-2 py-0.5 rounded-lg text-[11px] font-medium bg-gray-100 dark:bg-neutral-700 text-gray-700 dark:text-gray-200"
                            >
                              {name}
                            </span>
                          ))
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="mt-5 pt-3 border-t border-gray-100 dark:border-neutral-700 flex items-center justify-end gap-2">
                    <button
                      type="button"
                      onClick={() => handleOpenEditModal(k)}
                      className="px-3 py-1.5 rounded-xl text-xs font-semibold border border-gray-200 dark:border-neutral-700 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-neutral-700 flex items-center gap-1.5 transition"
                    >
                      <IconPencil size={14} />
                      Edit
                    </button>
                    <button
                      type="button"
                      onClick={() => handleDelete(k.id, k.name)}
                      className="px-3 py-1.5 rounded-xl text-xs font-semibold text-red-600 hover:bg-red-50 dark:hover:bg-red-950/30 flex items-center gap-1.5 transition"
                    >
                      <IconTrash size={14} />
                      Delete
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Add / Edit Modal */}
      {modalState.isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fade-in">
          <div className="bg-white dark:bg-neutral-800 rounded-3xl shadow-2xl max-w-lg w-full overflow-hidden border border-gray-100 dark:border-neutral-700">
            <div className="px-6 py-4 border-b border-gray-100 dark:border-neutral-700 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <IconChefHat className="text-emerald-600" size={22} />
                <h3 className="font-bold text-lg text-gray-900 dark:text-white">
                  {modalState.isEdit ? "Edit Kitchen Display" : "Add Kitchen Display"}
                </h3>
              </div>
              <button
                type="button"
                onClick={() => setModalState((prev) => ({ ...prev, isOpen: false }))}
                className="w-8 h-8 rounded-full flex items-center justify-center hover:bg-gray-100 dark:hover:bg-neutral-700 text-gray-500"
              >
                <IconX size={18} />
              </button>
            </div>

            <form onSubmit={handleSubmitModal} className="p-6 space-y-4 max-h-[80vh] overflow-y-auto">
              <div>
                <label className="block text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider mb-1.5">
                  Kitchen Name *
                </label>
                <input
                  type="text"
                  required
                  placeholder="e.g. Main Kitchen, Bar Station, Bakery"
                  value={modalState.name}
                  onChange={(e) => setModalState((prev) => ({ ...prev, name: e.target.value }))}
                  className="w-full text-sm rounded-xl px-4 py-2.5 border border-gray-200 dark:border-neutral-700 bg-gray-50/50 dark:bg-neutral-900 focus:outline-emerald-500"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider mb-1.5">
                  Description / Station Notes
                </label>
                <input
                  type="text"
                  placeholder="e.g. Hot cooked food, grill, and fried items"
                  value={modalState.description}
                  onChange={(e) => setModalState((prev) => ({ ...prev, description: e.target.value }))}
                  className="w-full text-sm rounded-xl px-4 py-2.5 border border-gray-200 dark:border-neutral-700 bg-gray-50/50 dark:bg-neutral-900 focus:outline-emerald-500"
                />
              </div>

              {/* Category Assignment Checklist */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                    Assign Categories ({modalState.selectedCategories.length} selected)
                  </label>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={handleSelectAllCategories}
                      className="text-xs text-emerald-600 hover:underline font-semibold"
                    >
                      Select All
                    </button>
                    <span className="text-gray-300">•</span>
                    <button
                      type="button"
                      onClick={handleClearAllCategories}
                      className="text-xs text-gray-500 hover:underline"
                    >
                      Clear All
                    </button>
                  </div>
                </div>

                <p className="text-[11px] text-gray-400 mb-2">
                  Check all categories that this kitchen screen will prepare. If none are selected, all categories will be visible.
                </p>

                {isLoadingCategories ? (
                  <p className="text-xs text-gray-400 py-3">Loading categories...</p>
                ) : allCategories.length === 0 ? (
                  <p className="text-xs text-amber-600 bg-amber-50 p-2.5 rounded-xl">
                    No categories found. You can create categories in Settings &gt; Menu Items.
                  </p>
                ) : (
                  <div className="grid grid-cols-2 gap-2 max-h-48 overflow-y-auto p-2 border border-gray-100 dark:border-neutral-700 rounded-2xl bg-gray-50/40 dark:bg-neutral-900/40">
                    {allCategories.map((c) => {
                      const idNum = Number(c.id);
                      const isSelected = modalState.selectedCategories.includes(idNum);

                      return (
                        <label
                          key={c.id}
                          className={`flex items-center gap-2 p-2 rounded-xl border text-xs cursor-pointer transition select-none ${
                            isSelected
                              ? "bg-emerald-50 border-emerald-300 text-emerald-800 dark:bg-emerald-950/40 dark:border-emerald-800 dark:text-emerald-300 font-semibold"
                              : "bg-white dark:bg-neutral-800 border-gray-200 dark:border-neutral-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50"
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => handleToggleCategory(c.id)}
                            className="checkbox checkbox-xs checkbox-success rounded"
                          />
                          <span className="truncate">{c.title}</span>
                        </label>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Status Switch */}
              <div className="flex items-center justify-between pt-2">
                <div>
                  <p className="text-xs font-bold text-gray-700 dark:text-gray-300">Kitchen Active</p>
                  <p className="text-[11px] text-gray-400">Enable this kitchen station on the kitchen display</p>
                </div>
                <input
                  type="checkbox"
                  checked={modalState.isActive}
                  onChange={(e) => setModalState((prev) => ({ ...prev, isActive: e.target.checked }))}
                  className="toggle toggle-success"
                />
              </div>

              <div className="pt-4 border-t border-gray-100 dark:border-neutral-700 flex items-center justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setModalState((prev) => ({ ...prev, isOpen: false }))}
                  className="px-4 py-2 rounded-xl text-xs font-semibold text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-neutral-700"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={modalState.isSubmitting}
                  className="px-5 py-2 rounded-xl text-xs font-bold bg-restro-green text-white hover:bg-emerald-700 transition shadow-sm disabled:opacity-50"
                >
                  {modalState.isSubmitting ? "Saving..." : modalState.isEdit ? "Update Kitchen" : "Create Kitchen"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </Page>
  );
}
